import type WebMap from '@arcgis/core/WebMap';
import { Box, Card, DismissableLayer, Flex, Slot } from '@volue/wave-react';
import type { LatLngBounds } from 'leaflet';
import React, { useEffect } from 'react';
import { Popup } from 'react-leaflet';

import useFeatureToggles from '@/common/hooks/feature-toggles';
import type { FeaturePickerGroup } from '@/common/overmind/namespaces/arcgis/config/config.types';
import { useWebMap } from '@/features/execute-calculation-set/components/Calculation/GridElementsCalculationTacticResults/useFeatureDetails';
import { useAppState } from '@/overmind';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import { useHighlightGridElementInMap } from './useHighlightGridElementInMap';

type GroupedFeatures = {
  title: string;
  assetTypes: (string | number)[];
  features: FeatureWithIDs[];
};

function useAutoSelectOnSingleFeature(
  select: (selected: FeatureWithIDs) => void,
  featuresToPickFrom: FeaturesInBounds
) {
  useEffect(() => {
    if (featuresToPickFrom.features.length === 1) {
      select(featuresToPickFrom.features[0]);
    }
  });
}

export type FeaturesInBounds = {
  bounds?: LatLngBounds;
  features: FeatureWithIDs[];
};

type Select = (newSelected: FeatureWithIDs) => void;

type FeaturePickerProps = {
  onSelected: Select;
  onClose: () => void;
  featuresToPickFrom: FeaturesInBounds;
};

export function FeaturePicker({
  onSelected,
  onClose,
  featuresToPickFrom
}: FeaturePickerProps) {
  const featuresInCalculationInLayer =
    useAppState().arcgis.featureGeometries.value;
  const featuresInCalculation =
    featuresInCalculationInLayer?.flatMap(f => f.features) ?? [];
  const featureFlags = useFeatureToggles();
  const webMap = useWebMap();

  const { highlight, stopHighlighting } = useHighlightGridElementInMap();

  const select = (selected: FeatureWithIDs) => {
   /*highlight(selected);*/
    onSelected(selected);
  };

  useAutoSelectOnSingleFeature(select, featuresToPickFrom);

  const closePopup = () => {
    stopHighlighting();
    onClose();
  };

  const subTypeGroups = useAppState().arcgis.featureTypes.value ?? {};

  if (webMap.type !== 'READY') return null;

  const singleOrNoSelectedFeatures = featuresToPickFrom.features.length < 2;
  if (singleOrNoSelectedFeatures || !featuresToPickFrom.bounds) {
    return null;
  }

  const availableGroups = mapToAvailableGroups(
    featuresToPickFrom.features,
    webMap.webMap
  );

  return (
    <span data-component={'build-calculation-context/.../GridImageLayer' + FeaturePicker.name} style={{ display: 'contents' }}>
      <Popup position={featuresToPickFrom.bounds.getCenter()}>
        <DismissableLayer as={Slot} onDismiss={() => closePopup()}>
          <Card
            css={{
              maxHeight: '20rem',
              paddingBlock: 0
            }}
            className="epsilon"
          >
            <Flex
              flow="column"
              gap="spacingM"
              css={{
                overflow: 'auto',
                padding: '$spacingL'
              }}
            >
              {availableGroups.map(group => (
                <Flex flow="column" gap="spacingXs" key={group.title}>
                  <Box css={{ color: '#939393', fontWeight: 600 }}>
                    {group.title}
                  </Box>
                  <Flex flow="column" gap="spacingS">
                    {group.features.map(feature => (
                      <Box
                        key={feature.gc.globalId}
                        onClick={e => {
                          e.stopPropagation();
                          onSelected({ ...feature });
                        }}
                        onMouseOver={e => {
                          e.stopPropagation();
                          highlight(feature);
                        }}
                        onMouseLeave={e => {
                          e.stopPropagation();
                          stopHighlighting();
                        }}
                        css={{
                          cursor: 'pointer',
                          paddingInline: '$spacingM',
                          '&:hover': {
                            backgroundColor: '$colorAccent10',
                            borderRadius: '$spacingXs'
                          }
                        }}
                      >
                        {getTitle(feature)}
                      </Box>
                    ))}
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Card>
        </DismissableLayer>
      </Popup>
    </span>
  );
  function getTitle(featureInPicker: FeatureWithIDs): React.ReactNode {
    if (featureFlags.isShowingGridElementTitleEnabled()) {
      const feature = featuresInCalculation?.find(
        f => f.gc.globalId === featureInPicker.gc.globalId
      );
      const title = feature?.gc.webMapTitle;
      if (title) return title;

      return feature?.gc.title;
    }
    return subTypeGroups[featureInPicker.gc.type as number];
  }
}

function mapToAvailableGroups(
  filteredFeatures: FeatureWithIDs[],
  webMap: WebMap
) {
  const allAvailableLayerGroups = toAllAvailableLayerGroups(webMap);
  const availableUnGroups = filteredFeatures.reduce<GroupedFeatures[]>(
    (availableGroups, feature) => {
      const group = allAvailableLayerGroups.find(group =>
        group.layerNames.includes(feature.gc.layerName)
      );
      const existingGroup = availableGroups.find(
        g => g.title === group?.groupName
      );
      if (existingGroup) {
        if (!isAlreadyAddedToGroup(existingGroup, feature)) {
          existingGroup.features.push(feature);
          existingGroup.assetTypes.push(feature.gc.type);
        }
      } else {
        availableGroups.push({
          title: group?.groupName ?? 'Others',
          assetTypes: [feature.gc.type],
          features: [feature]
        });
      }
      return availableGroups;
    },
    []
  );

  return availableUnGroups;

  function isAlreadyAddedToGroup(
    existingGroup: GroupedFeatures,
    feature: FeatureWithIDs
  ) {
    return existingGroup.features.find(
      f => f.gc.globalId === feature.gc.globalId
    );
  }

  function toAllAvailableLayerGroups(webMap: WebMap) {
    return webMap.allLayers.map(layer => {
      if (layer.type === 'subtype-group') {
        const groupLayer = layer as __esri.SubtypeGroupLayer;
        return {
          groupName: layer.title,
          layerNames: groupLayer.sublayers
            .map(sublayer => sublayer.title)
            .concat([layer.title])
        };
      }

      return {
        groupName: layer.title,
        layerNames: [layer.title]
      };
    });
  }
}
